/**
 * Menu 2 — mobile accordion navigation.
 *
 * <menu2-accordion data-single-open="true">
 *   <li data-accordion-item>
 *     <button data-accordion-trigger aria-expanded="false" aria-controls="id">…</button>
 *     <div data-accordion-panel id="id" inert>…</div>
 *   </li>
 * </menu2-accordion>
 *
 * Height animation is CSS-only (grid-template-rows 0fr → 1fr); this element
 * only manages state: .is-open, aria-expanded and `inert` on closed panels.
 */
class Menu2Accordion extends HTMLElement {
  connectedCallback() {
    this.singleOpen = this.dataset.singleOpen !== 'false';
    this.addEventListener('click', this.onClick);
    // All items start collapsed (the current page's item is only highlighted via .is-current).
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick = (event) => {
    const trigger = event.target.closest('[data-accordion-trigger]');
    if (!trigger || !this.contains(trigger)) return;

    const item = trigger.closest('[data-accordion-item]');
    if (!item) return;

    event.preventDefault();

    if (item.classList.contains('is-open')) {
      this.close(item);
      return;
    }

    if (this.singleOpen) {
      const siblings = item.parentElement.querySelectorAll(':scope > [data-accordion-item].is-open');
      siblings.forEach((sibling) => {
        if (sibling !== item) this.close(sibling);
      });
    }

    this.open(item);
  };

  open(item, { instant = false } = {}) {
    const trigger = item.querySelector(':scope > [data-accordion-trigger]');
    const panel = item.querySelector(':scope > [data-accordion-panel]');

    if (instant) {
      panel?.style.setProperty('transition', 'none');
      requestAnimationFrame(() => panel?.style.removeProperty('transition'));
    }

    item.classList.add('is-open');
    trigger?.setAttribute('aria-expanded', 'true');
    panel?.removeAttribute('inert');
  }

  close(item) {
    const trigger = item.querySelector(':scope > [data-accordion-trigger]');
    const panel = item.querySelector(':scope > [data-accordion-panel]');

    item.classList.remove('is-open');
    trigger?.setAttribute('aria-expanded', 'false');
    panel?.setAttribute('inert', '');

    // Collapse any nested items too so re-opening starts clean.
    item.querySelectorAll('[data-accordion-item].is-open').forEach((nested) => this.close(nested));
  }
}

if (!customElements.get('menu2-accordion')) {
  customElements.define('menu2-accordion', Menu2Accordion);
}


/**
 * Menu 2 — horizontally scrollable desktop nav with edge arrows
 * (same pattern as the Local theme's <scrollable-navigation>).
 *
 * <nav class="header__nav">
 *   <button data-nav-arrow="prev">…</button>
 *   <header-nav-scroller><ul class="header__nav-items">…</ul></header-nav-scroller>
 *   <button data-nav-arrow="next">…</button>
 * </nav>
 *
 * Adds `.is-scrollable` to the nav only while the links overflow, so the
 * scroller stays overflow:visible (and the megamenu hover bridge unclipped)
 * whenever everything fits. `data-scroll-start` / `data-scroll-end` on the
 * nav dim the arrow at the reached edge.
 */
class HeaderNavScroller extends HTMLElement {
  static SCROLL_STEP = 160;

  connectedCallback() {
    this.nav = this.closest('.header__nav');
    this.list = this.querySelector('.header__nav-items');
    if (!this.nav || !this.list) return;

    this.onArrowClick = (event) => {
      const arrow = event.target.closest('[data-nav-arrow]');
      if (!arrow || !this.nav.contains(arrow)) return;
      const direction = arrow.dataset.navArrow === 'prev' ? -1 : 1;
      this.scrollBy({ left: direction * HeaderNavScroller.SCROLL_STEP, behavior: 'smooth' });
    };
    this.onScroll = () => {
      this.updateEdges();
      // keep an open dropdown glued to its tab while scrolling
      this.dropdownItems?.forEach((item) => {
        if (item.querySelector(':scope > .js-dropdown.is-visible')) this.positionDropdown(item);
      });
    };
    this.onResize = () => this.check();

    this.nav.addEventListener('click', this.onArrowClick);
    this.addEventListener('scroll', this.onScroll, { passive: true });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this);
      this.resizeObserver.observe(this.list);
    } else {
      window.addEventListener('resize', this.onResize);
    }

    // Compact dropdowns are absolutely positioned against the header with no
    // explicit inline offset, so once the list is scrolled they'd open at the
    // tab's stale static position. Pin them to the tab's live position instead.
    this.dropdownItems = Array.from(this.list.querySelectorAll('.js-nav-item.dropdown'));
    this.onItemEnter = (event) => this.positionDropdown(event.currentTarget);
    this.dropdownItems.forEach((item) => {
      item.addEventListener('mouseenter', this.onItemEnter);
      item.addEventListener('focusin', this.onItemEnter);
    });

    // Belt and braces: whatever opens a dropdown (theme hover JS, keyboard,
    // touch), reposition the moment it becomes visible, and again once a
    // smooth scroll settles.
    if ('MutationObserver' in window) {
      this.dropdownObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          const dropdown = mutation.target;
          if (dropdown.classList.contains('is-visible')) {
            this.positionDropdown(dropdown.closest('.js-nav-item'));
          }
        }
      });
      this.dropdownItems.forEach((item) => {
        const dropdown = item.querySelector(':scope > .js-dropdown');
        if (dropdown) this.dropdownObserver.observe(dropdown, { attributes: true, attributeFilter: ['class'] });
      });
    }
    this.onScrollEnd = () => this.dropdownItems.forEach((item) => this.positionDropdown(item));
    this.addEventListener('scrollend', this.onScrollEnd);

    // Fonts loading late can change the list width.
    document.fonts?.ready.then(() => this.check());
    this.check();
  }

  /** Nearest positioned ancestor of the dropdown = its containing block. */
  containingBlock() {
    if (this._containingBlock) return this._containingBlock;
    let node = this.parentElement;
    while (node && node !== document.body) {
      if (getComputedStyle(node).position !== 'static') break;
      node = node.parentElement;
    }
    this._containingBlock = node || document.body;
    return this._containingBlock;
  }

  positionDropdown(item) {
    const dropdown = item.querySelector(':scope > .js-dropdown');
    if (!dropdown) return;
    const cb = this.containingBlock().getBoundingClientRect();
    const rect = item.getBoundingClientRect();
    const rtl = (document.documentElement.dir || '').toLowerCase() === 'rtl';
    let offset = rtl ? cb.right - rect.right : rect.left - cb.left;
    // Keep the dropdown inside its containing block so it can never widen the page
    // (compact dropdowns are width:max-content and open from the tab's left edge).
    const width = dropdown.offsetWidth;
    if (width) offset = Math.max(0, Math.min(offset, cb.width - width));
    dropdown.style.insetInlineStart = `${Math.round(offset)}px`;
  }

  disconnectedCallback() {
    this.dropdownObserver?.disconnect();
    this.removeEventListener('scrollend', this.onScrollEnd);
    this.dropdownItems?.forEach((item) => {
      item.removeEventListener('mouseenter', this.onItemEnter);
      item.removeEventListener('focusin', this.onItemEnter);
    });
    this.nav?.removeEventListener('click', this.onArrowClick);
    this.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.onResize);
  }

  check() {
    const overflowing = this.list.scrollWidth > this.clientWidth + 1;
    this.nav.classList.toggle('is-scrollable', overflowing);
    if (!overflowing) this.scrollLeft = 0;
    this.updateEdges();
  }

  updateEdges() {
    const max = this.scrollWidth - this.clientWidth;
    this.nav.toggleAttribute('data-scroll-start', this.scrollLeft <= 1);
    this.nav.toggleAttribute('data-scroll-end', this.scrollLeft >= max - 1);
  }
}

if (!customElements.get('header-nav-scroller')) {
  customElements.define('header-nav-scroller', HeaderNavScroller);
}


/**
 * Menu 2 — full-height mega panel support.
 * Publishes the header's live bottom edge (viewport px) as --menu2-header-bottom
 * so the panel can be sized `100dvh - that`, regardless of the announcement bar
 * scrolling away or the sticky header changing height.
 */
(() => {
  const header = document.querySelector('#header');
  if (!header) return;
  let frame = 0;
  const update = () => {
    frame = 0;
    const bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
    document.documentElement.style.setProperty('--menu2-header-bottom', `${bottom}px`);
  };
  const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);
  header.addEventListener('mouseenter', schedule, true);
  header.addEventListener('focusin', schedule);
  if ('ResizeObserver' in window) new ResizeObserver(schedule).observe(header);
  document.addEventListener('shopify:section:load', schedule);
  update();
})();
